import type { GraphicsRequirements } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { Plugin } from '@/shared/types/plugin'
import type { ViewRect, ViewSnapshot } from '@/shared/types/view'
import { ViewRegistryImpl } from './ViewRegistry'

/**
 * 视图插件，注册 ViewRegistry 服务并维护 primary view 与辅助节点挂接。
 */
export class ViewPlugin implements Plugin {
  /** 插件名称 */
  readonly name = 'view'
  /** 插件启动优先级 */
  readonly priority = 8
  readonly graphicsRequirements: GraphicsRequirements = Object.freeze({
    backends: Object.freeze(['webgl2', 'webgpu'] as const),
    capabilities: Object.freeze(['multiView'] as const),
  })
  /** 对外提供的服务名列表 */
  readonly provides = ['ViewRegistry'] as const

  private context: EngineContext | null = null
  private registry: ViewRegistryImpl | null = null
  private disposed = false
  private primaryViewTracksViewport = true
  private syncingPrimaryViewRect = false
  private readonly handleViewCreated = (event: { viewId: string }) => {
    this.ensureHelperRoots(event.viewId)
  }
  private readonly handleViewRemoved = (event: { viewId: string }) => {
    this.context?.sceneGraph.removeViewHelperRoots(event.viewId)
  }
  private readonly handleViewUpdated = (event: {
    viewId: string
    snapshot: ViewSnapshot
    previous: ViewSnapshot
  }) => {
    if (!this.registry || event.viewId !== this.registry.primaryViewId) {
      return
    }

    if (this.syncingPrimaryViewRect || rectEquals(event.snapshot.rect, event.previous.rect)) {
      return
    }

    this.primaryViewTracksViewport = this.isFullViewportRect(event.snapshot.rect)
  }
  private readonly handleViewportResized = (event: { width: number; height: number }) => {
    this.syncPrimaryViewRect(event.width, event.height)
  }

  /**
   * 初始化插件，注册 ViewRegistry 服务。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.registry = new ViewRegistryImpl(context.events)
    context.registerService('ViewRegistry', this.registry)
  }

  /** 启动插件，创建 primary view 并绑定辅助节点 */
  onStart(): void {
    if (!this.context || !this.registry) {
      throw new Error('ViewPlugin 尚未初始化')
    }

    this.context.events.on('view.created', this.handleViewCreated)
    this.context.events.on('view.removed', this.handleViewRemoved)
    this.context.events.on('view.updated', this.handleViewUpdated)
    this.context.events.on('viewport.resized', this.handleViewportResized)

    if (!this.registry.get(this.registry.primaryViewId)) {
      const { width, height } = this.context.getSize()
      this.registry.create({
        id: this.registry.primaryViewId,
        rect: { x: 0, y: 0, width, height },
        visible: true,
        zIndex: 0,
        layerMask: 1,
        userData: {},
        removable: false,
      })
      this.primaryViewTracksViewport = true
    } else {
      const primary = this.registry.get(this.registry.primaryViewId)
      this.primaryViewTracksViewport = primary ? this.isFullViewportRect(primary.rect) : true
    }

    for (const view of this.registry.list()) {
      this.ensureHelperRoots(view.id)
    }
  }

  /** 销毁插件，释放 ViewRegistry 与辅助节点 */
  onDestroy(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.context?.events.off('view.created', this.handleViewCreated)
    this.context?.events.off('view.removed', this.handleViewRemoved)
    this.context?.events.off('view.updated', this.handleViewUpdated)
    this.context?.events.off('viewport.resized', this.handleViewportResized)
    this.registry?.dispose()
    this.registry = null
    this.context = null
  }

  private ensureHelperRoots(viewId: string): void {
    if (!this.context || !this.registry) {
      return
    }

    const helperRoots = this.context.sceneGraph.createViewHelperRoots(viewId)
    this.registry.setExtension(viewId, 'toolsRoot', helperRoots.toolsRoot)
    this.registry.setExtension(viewId, 'overlayRoot', helperRoots.overlayRoot)
    this.registry.setExtension(viewId, 'debugRoot', helperRoots.debugRoot)
  }

  private syncPrimaryViewRect(width: number, height: number): void {
    if (!this.registry || !this.primaryViewTracksViewport) {
      return
    }

    const primary = this.registry.get(this.registry.primaryViewId)
    if (!primary || rectEquals(primary.rect, { x: 0, y: 0, width, height })) {
      return
    }

    this.syncingPrimaryViewRect = true
    try {
      this.registry.setRect(this.registry.primaryViewId, { x: 0, y: 0, width, height })
    } finally {
      this.syncingPrimaryViewRect = false
    }
  }

  private isFullViewportRect(rect: ViewRect): boolean {
    if (!this.context) {
      return false
    }

    const { width, height } = this.context.getSize()
    return rectEquals(rect, { x: 0, y: 0, width, height })
  }
}

function rectEquals(left: ViewRect, right: ViewRect): boolean {
  return (
    left.x === right.x &&
    left.y === right.y &&
    left.width === right.width &&
    left.height === right.height
  )
}
